import React, { useCallback, useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { getActivityLogs, exportActivityLogsXlsx } from "@/lib/api";
import { toast } from "sonner";
import { History, FlaskConical, Droplets, Package, Search, X, FileSpreadsheet } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n/I18nContext";
import { formatDateTime } from "@/lib/dates";
import { usePagination } from "@/hooks/usePagination";
import { TablePagination } from "@/components/TablePagination";

const actionBadge = {
  created: "badge-ok",
  updated: "badge-warn",
  deleted: "badge-bad",
  repacked: "badge-warn",
  adjusted: "badge-warn",
  out_of_freezer: "badge-warn",
  dispatched: "badge-bad",
  renamed: "badge-warn",
  writeoff_reverted: "badge-ok",
};

const KNOWN_ACTIONS = ["created", "updated", "deleted", "repacked", "adjusted", "out_of_freezer", "dispatched", "renamed", "writeoff_reverted"];

export default function ActivityLog() {
  const { t, lang } = useT();
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState(""); // debounced value actually sent to the API
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const pager = usePagination(logs, 50);

  // Debounce the search input so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  const entityMeta = {
    production: { label: t("activity.production"), Icon: FlaskConical },
    milk_batch: { label: t("planning.milkReception"), Icon: Droplets },
    stock_lot: { label: t("activity.stockLot"), Icon: Package },
  };

  const fmtVal = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "object") return t("activity.detailsVal");
    return String(v);
  };

  // Friendly labels for change chips (raw field names otherwise).
  const fieldLabel = (f) => (f === "production_code" ? t("prod.lotNumber") : f);

  const load = useCallback(async () => {
    const params = filter === "all" ? {} : { entity_type: filter };
    if (query) params.q = query;
    if (startDate) params.start = startDate;
    if (endDate) params.end = endDate;
    if (query || startDate || endDate) params.limit = 1000; // dig deeper into history when narrowing
    setLogs(await getActivityLogs(params));
  }, [filter, query, startDate, endDate]);
  useEffect(() => { load(); }, [load]);

  const [exporting, setExporting] = useState(false);
  // Excel export honours the same filters as the on-screen list (type, search, date range).
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const params = filter === "all" ? {} : { entity_type: filter };
      if (query) params.q = query;
      if (startDate) params.start = startDate;
      if (endDate) params.end = endDate;
      params.lang = lang;
      await exportActivityLogsXlsx(params);
      toast.success(t("activity.exported"));
    } catch {
      toast.error(t("activity.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("activity.title")} ({logs.length})
          </CardTitle>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex items-center gap-1.5" data-testid="activity-date-range">
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="h-9 w-[8.5rem]" aria-label={t("activity.fromDate")} data-testid="activity-start-date" />
              <span className="text-xs text-muted-foreground">–</span>
              <Input type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)}
                className="h-9 w-[8.5rem]" aria-label={t("activity.toDate")} data-testid="activity-end-date" />
              {(startDate || endDate) && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground"
                  onClick={() => { setStartDate(""); setEndDate(""); }}
                  data-testid="activity-date-clear" aria-label={t("common.clear")}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("activity.searchPlaceholder")}
                className="h-9 w-full pl-8 pr-8 sm:w-72"
                data-testid="activity-search-input"
              />
              {search && (
                <Button variant="ghost" size="icon" onClick={() => setSearch("")}
                  className="absolute right-0.5 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground"
                  data-testid="activity-search-clear" aria-label={t("common.clear")}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList data-testid="activity-filter-tabs">
                <TabsTrigger value="all" data-testid="activity-filter-all">{t("activity.all")}</TabsTrigger>
                <TabsTrigger value="production" data-testid="activity-filter-production">{t("activity.productions")}</TabsTrigger>
                <TabsTrigger value="stock_lot" data-testid="activity-filter-stock">{t("activity.stock")}</TabsTrigger>
                <TabsTrigger value="milk_batch" data-testid="activity-filter-milk">{t("planning.milkReception")}</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button variant="outline" size="sm" className="h-9" onClick={exportXlsx} disabled={exporting || logs.length === 0} data-testid="activity-export-xlsx">
              <FileSpreadsheet className="mr-1.5 h-4 w-4" /> {exporting ? t("activity.exporting") : t("activity.exportXlsx")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center" data-testid="activity-empty-state">
              <History className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">
                {query ? t("activity.noSearchResults", { q: query })
                  : (startDate || endDate) ? t("activity.noRangeResults")
                  : t("activity.empty")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("activity.when")}</TableHead>
                    <TableHead>{t("activity.user")}</TableHead>
                    <TableHead>{t("activity.action")}</TableHead>
                    <TableHead>{t("activity.item")}</TableHead>
                    <TableHead>{t("activity.details")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pager.pageItems.map((a) => {
                    const meta = entityMeta[a.entity_type] || { label: a.entity_type, Icon: History };
                    const Icon = meta.Icon;
                    return (
                      <TableRow key={a.id} data-testid={`activity-row-${a.id}`}>
                        <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                          {formatDateTime(a.timestamp)}
                        </TableCell>
                        <TableCell className="text-sm font-medium">{a.user}</TableCell>
                        <TableCell><Badge className={actionBadge[a.action] || "badge-warn"}>{KNOWN_ACTIONS.includes(a.action) ? t(`activity.${a.action}`) : a.action}</Badge></TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5 text-sm">
                            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-mono text-xs">{a.entity_code || meta.label}</span>
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {a.summary && <div>{a.summary}</div>}
                          {(a.changes || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {a.changes.map((c, i) => (
                                <span key={`${c.field}-${i}`} className="rounded bg-secondary px-1.5 py-0.5 text-[11px]">
                                  <span className="font-medium">{fieldLabel(c.field)}</span>: {fmtVal(c.old)} → {fmtVal(c.new)}
                                </span>
                              ))}
                            </div>
                          )}
                          {!a.summary && (a.changes || []).length === 0 && "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                page={pager.page} pageCount={pager.pageCount} pageSize={pager.pageSize}
                total={pager.total} start={pager.start} end={pager.end}
                onPageChange={pager.setPage} onPageSizeChange={pager.setPageSize}
                testid="activity-pagination"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}
